/**
 * Enrollment Flow Event Tracking
 * Tracks user journey through event/course enrollment
 */

import { posthog } from '../posthog';
import type {
  EnrollmentStartedProperties,
  EnrollmentTicketSelectedProperties,
  EnrollmentPromocodeAppliedProperties,
  EnrollmentCustomFieldsShownProperties,
  EnrollmentCustomFieldsCompletedProperties,
  EnrollmentProfileModalShownProperties,
  EnrollmentProfileModalCompletedProperties,
  EnrollmentCompletedProperties,
  EnrollmentAbandonedProperties,
} from '../types';

// Store enrollment start times for calculating duration
const enrollmentStartTimes = new Map<string, number>();

/**
 * Mask promocode for privacy (show only first 3 chars)
 */
function maskPromocode(code: string): string {
  if (code.length <= 3) return code;
  return code.substring(0, 3) + '***';
}

export const enrollmentEvents = {
  /**
   * Track when user starts enrollment process
   * Trigger: User clicks "Register" or "Enroll Now" button
   */
  started: (props: Omit<EnrollmentStartedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    enrollmentStartTimes.set(props.content_id, timestamp);

    posthog.capture('enrollment_started', {
      ...props,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track when user selects a ticket
   * Trigger: User selects ticket type and quantity
   */
  ticketSelected: (props: Omit<EnrollmentTicketSelectedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_ticket_selected', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track promocode application attempts
   * Trigger: User submits a promocode
   */
  promocodeApplied: (props: Omit<EnrollmentPromocodeAppliedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_promocode_applied', {
      ...props,
      promocode_code: maskPromocode(props.promocode_code),
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when custom fields form is shown
   * Trigger: Custom fields modal opens
   */
  customFieldsShown: (props: Omit<EnrollmentCustomFieldsShownProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_custom_fields_shown', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when custom fields are completed
   * Trigger: User submits custom fields form
   */
  customFieldsCompleted: (props: Omit<EnrollmentCustomFieldsCompletedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_custom_fields_completed', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when profile completion modal is shown
   * Trigger: Profile modal appears due to missing required fields
   */
  profileModalShown: (props: Omit<EnrollmentProfileModalShownProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_profile_modal_shown', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when profile modal is completed
   * Trigger: User completes profile modal
   */
  profileModalCompleted: (props: Omit<EnrollmentProfileModalCompletedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('enrollment_profile_modal_completed', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track successful enrollment completion
   * Trigger: Enrollment record created successfully
   */
  completed: (props: Omit<EnrollmentCompletedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const startTime = enrollmentStartTimes.get(props.content_id);
    const timeToComplete = startTime ? timestamp - startTime : undefined;

    // Clean up stored start time
    enrollmentStartTimes.delete(props.content_id);

    posthog.capture('enrollment_completed', {
      ...props,
      time_to_complete_ms: props.time_to_complete_ms ?? timeToComplete,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track enrollment abandonment
   * Trigger: User exits enrollment flow without completing
   */
  abandoned: (props: Omit<EnrollmentAbandonedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const startTime = enrollmentStartTimes.get(props.content_id);
    const timeSpent = startTime ? timestamp - startTime : props.time_spent_ms;

    // Clean up stored start time
    enrollmentStartTimes.delete(props.content_id);

    posthog.capture('enrollment_abandoned', {
      ...props,
      time_spent_ms: timeSpent,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Clear stored enrollment start time (e.g., on page unmount)
   */
  clearStartTime: (contentId: string) => {
    enrollmentStartTimes.delete(contentId);
  },

  /**
   * Get time elapsed since enrollment started
   */
  getTimeElapsed: (contentId: string): number | undefined => {
    const startTime = enrollmentStartTimes.get(contentId);
    return startTime ? Date.now() - startTime : undefined;
  },
};
